unit uLockerFieldBindings;
(*
  PROMPT LOCKERS -- Field Bindings
  =================================
  UPDATED: 5/21/26
    - LoadBufferFromStorage: changed to more places to change FBuffer.Values[LBL_AI_PROMPT+':'+FN_AI_PROMPT]
      to FBuffer.Values[FN_AI_PROMPT] because the right side was only using FN_AI_PROMPT

  UPDATED: 5/16/26
    - BufVal: requires compound keys (Length(parts) > 1)
    - OnUIToBuffer for Parameters: manual ':' split with JSON array detection for options
      to avoid corruption when options value contains colons
    - OnBufferToUI for Parameters: handles JSON array values from DB (TJSONArray check)
    - LoadBufferFromStorage: Tags converted from JSON array to CommaText before storing
    - SendBufferToStorage: Tags handles both comma-delimited and JSON array formats from DB

  UPDATED: 5/7/26
    - BuildPipelines simplified: AddField handles all TLockerControl descendants
      uniformly via base class .Text; AddPIField handles property inspector rows.
      Three separate helpers (AddEditField/AddMemoField/AddComboField) collapsed
      into one.
    - Tags moved into Misc pipeline; TagsPipe removed.

  UPDATED: 5/2/26
    - Merged from uLockerStorageBinding + TLockerUIBinding into single unit
    - All four pumping methods consolidated here
    - FUIBuffer/FDBBuffer replaced by single FBuffer from base class
    - Pipeline definitions use FN_* / LBL_* constants from uPipelineFields
    - Constructor simplified to no-arg; buffers internal to base class

  All four pumping methods consolidated here:

    LoadWidgetsFromBuffer   -- Buffer  --> UI widgets
    SendWidgetsToBuffer     -- UI widgets --> Buffer
    LoadBufferFromStorage   -- Storage --> Buffer
    SendBufferToStorage     -- Buffer  --> Storage

  Adding or renaming a field means editing this unit only.

  The form reference is in the implementation uses clause to avoid
  a circular unit reference. TLockerEditor2_form is only needed
  inside method bodies, not in the class declaration.
*)

interface

uses
  System.SysUtils, System.Classes, System.StrUtils,
  WEBLib.REST, WEBLib.JSON, JS, Web,
  uLockerContracts, uPipelineFields, uLockerControls, uStrLists;

type
  TLockerFieldBindings = class(TAbstractFieldBindings)
  private
    FMetadataJSON : TJSONObject;
    FPromptJSON   : TJSONObject;
    FPipelines    : TPipelineSet;

    procedure BuildPipelines;

  public
    constructor Create;

    // UI <--> Buffer
    procedure LoadWidgetsFromBuffer; override;
    procedure SendWidgetsToBuffer;   override;

    // Buffer <--> Storage
    [async] function LoadBufferFromStorage(const aLockerID: string): string; async; override;
    [async] function SendBufferToStorage(const aLockerID: string): string; async;   override;

    function MetadataJSON : TJSONObject; override;
    function PromptJSON   : TJSONObject; override;
  end;

implementation

uses
  uConstants, LockerEditor2, uLockerController, uCommonStuff;

// ── Helpers ─────────────────────────────────────────────────────────────────

//===============================================================
function BufVal(aBuffer: TStringlist; const aKey: string; const aDflt: string = ''): string;
//===============================================================
var
  i     : integer;
  parts : TStringArray;
begin
  Result := aDflt;
  for i := 0 to aBuffer.Count-1 do
  begin
    parts := aBuffer.Names[i].Split([':']);
    if ((Length(parts) > 1) and (parts[1] = aKey)) or ((Length(parts) = 1) and (parts[0] = aKey)) then
      Exit(Trim(aBuffer.ValueFromIndex[i]));
  end;
end;

// ── Constructor ─────────────────────────────────────────────────────────────

//===============================================================
constructor TLockerFieldBindings.Create;
//===============================================================
begin
  inherited Create;
  FPipelines := TPipelineSet.Create;
end;

// ── Pipeline construction ────────────────────────────────────────────────────
(*
  Each pipeline groups related fields.
  Local helper procs eliminate repetitive closure boilerplate.
  All control access goes through uLockerControls wrappers --
  no raw .Text, .Cells, or .Caption references in this method.

  To add a field:
    1. Add FN_* / LBL_* constants to uPipelineFields
    2. Add an AddField or AddPIField call in the appropriate pipeline below
    3. Done -- all four pumps pick it up automatically

  To rename a field:
    1. Change the FN_* constant in uPipelineFields
    2. Done
*)

//===============================================================
procedure TLockerFieldBindings.BuildPipelines;
//===============================================================
var
  form       : TLockerEditor2_form;
  NameDescr  : TPipeline;
  AIConfig   : TPipeline;
  Metrics    : TPipeline;
  Misc       : TPipeline;
  ParamsPipe : TPipeline;
  fld        : TPipelineField;
  NameDescrPI: TLockerPropertyInspector;
  MetricsPI  : TLockerPropertyInspector;

  //-------------------------------------------------
  // Any TLockerControl descendant (Edit, Memo, Combo, Label...)
  procedure AddField(aPipeline: TPipeline; const aFN, aLBL: string; aCtrl: TLockerControl);
  var
    fn : string;
  begin
    fld := TPipelineField.Default(aFN, aLBL);
    fn  := fld.InternalName;  // capture before fld changes
    fld.OnUIToBuffer := procedure begin FBuffer.Values[fn] := aCtrl.Text end;
    fld.OnBufferToUI := procedure begin aCtrl.Text := BufVal(FBuffer, fn) end;

    aPipeline.AddField(fld);
  end;
  //-------------------------------------------------
  // Property inspector row
  procedure AddPIField(aPipeline: TPipeline; aPI: TLockerPropertyInspector;
                       const aFN, aLBL: string; aRow: integer;
                       aOnBufferToUI: TProc = nil);
  var
    fn          : string;
    capturedPI  : TLockerPropertyInspector;
    capturedRow : integer;
  begin
    fld := TPipelineField.Default(aFN, aLBL);
    fn          := fld.InternalName;
    capturedPI  := aPI;
    capturedRow := aRow;
    aPI.Names[aRow] := aLBL;
    fld.OnUIToBuffer := procedure begin FBuffer.Values[fn] := capturedPI.ValueFromIndex[capturedRow] end;
    if Assigned(aOnBufferToUI) then
      fld.OnBufferToUI := aOnBufferToUI
    else
      fld.OnBufferToUI := procedure begin capturedPI.ValueFromIndex[capturedRow] := BufVal(FBuffer, fn) end;

    aPipeline.AddField(fld);
  end;
  //-------------------------------------------------

begin
  form := LockerEditor2_form;

  NameDescrPI := TLockerPropertyInspector.Create(form.NameDescr_grid);
  MetricsPI   := TLockerPropertyInspector.Create(form.Metrics_grid);

  // ── NameDescr pipeline ───────────────────────────────────────────────────
  NameDescr := TPipeline.Create('NameDescr', FBuffer, FBuffer, FMetadataJSON);

  AddPIField(NameDescr, NameDescrPI, FN_LOCKER_ID, LBL_LOCKER_ID, 1,
    procedure begin
      NameDescrPI.ValueFromIndex[1]        := BufVal(FBuffer, FN_LOCKER_ID);
      form.LockerInfo_LockerID_lbl.Caption := BufVal(FBuffer, FN_LOCKER_ID);
    end);

  AddPIField(NameDescr, NameDescrPI, FN_NAME, LBL_NAME, 2,
    procedure begin
      NameDescrPI.ValueFromIndex[2]           := BufVal(FBuffer, FN_NAME);
      form.LockerInfo_LockerTitle_lbl.Caption := BufVal(FBuffer, FN_NAME);
    end);

  AddPIField(NameDescr, NameDescrPI, FN_TITLE,       LBL_TITLE,       3);
  AddPIField(NameDescr, NameDescrPI, FN_DESCRIPTION, LBL_DESCRIPTION, 4);
  AddPIField(NameDescr, NameDescrPI, FN_HOW_TO_USE,  LBL_HOW_TO_USE,  5);
  AddPIField(NameDescr, NameDescrPI, FN_HELP_TEXT,   LBL_HELP_TEXT,   6);
  AddPIField(NameDescr, NameDescrPI, FN_HELP_URL,    LBL_HELP_URL,    7);

  FPipelines.Add(NameDescr);

  // ── AIConfig pipeline ────────────────────────────────────────────────────
  AIConfig := TPipeline.Create('AIConfig', FBuffer, FBuffer, FMetadataJSON);

  AddField(AIConfig, FN_AI_PROMPT,   LBL_AI_PROMPT,   TLockerMemo.Create(form.AIPrompt_mmo));
  AddField(AIConfig, FN_AI_PLATFORM, LBL_AI_PLATFORM, TLockerCombo.Create(form.AIPlatform_cbo));
  AddField(AIConfig, FN_AI_MODEL,    LBL_AI_MODEL,    TLockerCombo.Create(form.AIModel_cbo));
  AddField(AIConfig, FN_TEMPERATURE, LBL_TEMPERATURE, TLockerEdit.Create(form.Temperature_edt));
  AddField(AIConfig, FN_MAX_TOKENS,  LBL_MAX_TOKENS,  TLockerEdit.Create(form.MaxTokens_edt));

  FPipelines.Add(AIConfig);

  // ── Misc pipeline ────────────────────────────────────────────────────────
  Misc := TPipeline.Create('Misc', FBuffer, FBuffer, FMetadataJSON);

  AddField(Misc, FN_SOURCE, LBL_SOURCE, TLockerEdit.Create(form.Source_edt));
  AddField(Misc, FN_AUTHOR, LBL_AUTHOR, TLockerEdit.Create(form.Author_edt));
  AddField(Misc, FN_PRICE,  LBL_PRICE,  TLockerEdit.Create(form.Price_edt));
  AddField(Misc, FN_TAGS,   LBL_TAGS,   TLockerCommaTextMemo.Create(form.Tags_mmo));

  FPipelines.Add(Misc);

  // ── Metrics pipeline ─────────────────────────────────────────────────────
  Metrics := TPipeline.Create('Metrics', FBuffer, FBuffer, FMetadataJSON);

  AddPIField(Metrics, MetricsPI, FN_VERSION,          LBL_VERSION,       1);
  AddPIField(Metrics, MetricsPI, FN_CREATED_AT,       LBL_CREATED_AT,    2);
  AddPIField(Metrics, MetricsPI, FN_LAST_EDITED_AT,   LBL_LAST_EDITED,   3);
  AddPIField(Metrics, MetricsPI, FN_EXECUTION_COUNT,  LBL_EXEC_COUNT,    4);
  AddPIField(Metrics, MetricsPI, FN_LAST_EXECUTED_AT, LBL_LAST_EXECUTED, 5);
  AddPIField(Metrics, MetricsPI, FN_TOTAL_REVENUE,    LBL_TOTAL_REVENUE, 6);

  FPipelines.Add(Metrics);

  // ── Parameters pipeline ──────────────────────────────────────────────────
  // Parameters_lbox with attached TStringlists is too specialized for a
  // generic wrapper -- keep custom procs here.
  ParamsPipe := TPipeline.Create('Parameters', FBuffer, FBuffer, FMetadataJSON);

  fld := TPipelineField.Default(FN_PARAMETERS, LBL_PARAMETERS);

  fld.OnUIToBuffer := procedure
    var i         : integer;
        csl       : TColonStrList;
        ParamsArr : TJSONArray;
        ParamObj  : TJSONObject;
    begin
      ParamsArr := TJSONArray.Create;
      for i := 0 to form.Parameters_lbox.Items.Count-1 do
      begin
        csl := TColonStrList(form.Parameters_lbox.Items.Objects[i]);
        if not Assigned(csl) then Continue;

        if (csl.Values['name'] = 'focus_area') then
          console.log('focus_area sl.Values[options]: ' + csl.Values['options']);  // $%^ ###

{        ParamObj := TJSONObject.Create;
        for k := 0 to sl.Count-1 do
        begin
          p := Pos(':', sl.Strings[k]);
          if (p > 0) then
          begin
            nm  := Copy(sl.Strings[k], 1, p-1);
            val := Copy(sl.Strings[k], p+1, MaxInt);
            if (nm = 'options') and StartsStr('[', val) then
              ParamObj.AddPair(nm, TJSONObject.ParseJSONValue(val) as TJSONArray)
            else
              ParamObj.AddPair(nm, TJSONString.Create(val));
          end;
        end;
}
        ParamObj := csl.AsJSONObj;
        ParamsArr.AddElement(ParamObj);

        console.log('added param: ' + ParamObj.ToJSON);  // $%^ ###

      end;
      FBuffer.Values[FN_PARAMETERS] := ParamsArr.ToString;

      console.log('>>> TLockerFieldBindings.BuildPipelines.fld.OnUIToBuffer');   // $%^ ###
      console.log('>>> - Tags_mmo.Text : {'+form.Tags_mmo.Text+'}');   // $%^ ###
    end;

  fld.OnBufferToUI := procedure
    var j, k  : integer;
        sl    : TColonStrList;
        jarr  : TJSONArray;
        jobj  : TJSONObject;
        s     : string;
    begin
      s := FBuffer.Values[FN_PARAMETERS];
      if (Trim(s) = '') or (Trim(s) = '[]') then Exit;
      form.Parameters_lbox.Items.Clear;
      jarr := TJSONObject.ParseJSONValue(s) as TJSONArray;
      if Assigned(jarr) then
        for j := 0 to jarr.Count-1 do
        begin
          jobj := jarr.Items[j] as TJSONObject;
          sl := TColonStrList.Create;
          for k := 0 to jobj.Count-1 do
          begin
            if (jobj.Pairs[k].JsonValue is TJSONArray) then
              sl.Values[jobj.Pairs[k].JsonString.Value] :=
                        jobj.Pairs[k].JsonValue.ToString
            else
              sl.Values[jobj.Pairs[k].JsonString.Value] :=
                        jobj.Pairs[k].JsonValue.Value;
          end;
          form.Parameters_lbox.Items.AddObject(sl.Values['name'], sl);
        end;
      form.Parameters_GridEditBlock.ClearGrid;
      form.CurrActivity_lbl.Show;
      form.Editor_TestLocker_pnlbtn.Visible := True;
    end;

  ParamsPipe.AddField(fld);
  FPipelines.Add(ParamsPipe);
end;

// ── UI <--> Buffer ───────────────────────────────────────────────────────────

procedure TLockerFieldBindings.LoadWidgetsFromBuffer;
begin
  if FPipelines.Count = 0 then
    BuildPipelines;
  FPipelines.PumpAllBufferToUI;
end;

procedure TLockerFieldBindings.SendWidgetsToBuffer;
begin
  if FPipelines.Count = 0 then
    BuildPipelines;

  console.log('SendWidgetsToBuffer: pipeline count = ' + IntToStr(FPipelines.Count));  // $%^ ###

  FPipelines.PumpAllUIToBuffer;
end;

// ── Buffer <--> Storage ──────────────────────────────────────────────────────

function TLockerFieldBindings.LoadBufferFromStorage(const aLockerID: string): string;
var
  Request          : THTTPRequest;
  req              : TJSXMLHttpRequest;
  MetadataResponse : TJSONObject;
  PromptsResponse  : TJSONObject;
  map              : TStringlist;
  i                : integer;
  fn               : string;
  //------------------------------------
  function JVal(const aKey: string): string; inline;
  begin
    Result := _JSON_VAL_STR(MetadataResponse, aKey);
    if (Result = 'null') then
      Result := '';
  end;
  //------------------------------------
  function JArr(const aKey: string): string; inline;
  begin
    Result := _JSON_VAL_ARR(MetadataResponse, aKey).ToString;
  end;
  //------------------------------------
begin
  Result := '';

  if Trim(aLockerID) = '' then
    Exit( 'No Locker selected' );

  FBuffer.Clear;

  Request         := THTTPRequest.Create(nil);
  Request.URL     := LOCKER_METADATA_API_URL + '?lockerID=' + aLockerID;
  Request.Command := httpGET;
  Request.Headers.AddPair('X-API-Key', LOCKER_STORAGE_API_KEY);

  req := await(TJSXMLHttpRequest, Request.Perform());

  if req.Status <> 200 then
    Exit( OpenRouterError(TJSONObject.ParseJSONValue(string(req.Response)) as TJSONObject,
                              nil, 'Error fetching metadata') );

  FMetadataJSON    := TJSONObject.ParseJSONValue(string(req.Response)) as TJSONObject;
  MetadataResponse := FMetadataJSON;

  // Scalar fields --> buffer via FIELD_NAME_MAP
  map := TStringlist.Create;
  map.NameValueSeparator := ':';
  map.AddStrings(FIELD_NAME_MAP);

  for i := 0 to map.Count-1 do
  begin
    fn := map.ValueFromIndex[i];
    if MatchText(fn, [FN_LOCKER_ID, FN_TAGS, FN_PARAMETERS, FN_AI_PROMPT]) then
      Continue;
    FBuffer.Values[fn] := JVal(fn);
  end;

  // Special cases
  FBuffer.Values[FN_LOCKER_ID] := aLockerID;

  // Tags: store raw JSON array string
  FBuffer.Values[FN_TAGS] := JArr(FN_TAGS);

//console.log('>>> LoadBufferFromStorage: ');
//console.log('-- csv.CommaText in buffer: [' + csv.CommaText + ']');  // $%^ ###
//console.log('-- raw tags JSON: {'+JArr(FN_TAGS)+'}');
//console.log('-- FBuffer tags: {'+FBuffer.Values[FN_TAGS]+'}');

  // Parameters
  FBuffer.Values[FN_PARAMETERS] := JArr(FN_PARAMETERS);


  FBuffer.Values[FN_TAGS] := JArr(FN_TAGS);

//console.log('>>> LoadBufferFromStorage: ');
//console.log('-- csv.CommaText in buffer: [' + csv.CommaText + ']');  // $%^ ###
//console.log('-- raw tags JSON: {'+JArr(FN_TAGS)+'}');
//console.log('-- FBuffer tags: {'+FBuffer.Values[FN_TAGS]+'}');

  // Parameters
  FBuffer.Values[FN_PARAMETERS] := JArr(FN_PARAMETERS);

  // Fetch prompt
  Request.URL := LOCKER_PROMPTS_API_URL + '?lockerID=' + aLockerID;
  req := await(TJSXMLHttpRequest, Request.Perform());

  if req.Status <> 200 then
    Exit( OpenRouterError(TJSONObject.ParseJSONValue(string(req.Response)) as TJSONObject,
                              nil, 'Error fetching prompt') );

  PromptsResponse  := TJSONObject.ParseJSONValue(string(req.Response)) as TJSONObject;
  FPromptJSON      := PromptsResponse;
  FBuffer.Values[FN_AI_PROMPT] := _JSON_VAL_STR(PromptsResponse, FN_AI_PROMPT);

end;

function TLockerFieldBindings.SendBufferToStorage(const aLockerID: string): string;
var
  Request    : THTTPRequest;
  req        : TJSXMLHttpRequest;
  MetaObj    : TJSONObject;
  ParamsArr  : TJSONArray;
  PromptObj  : TJSONObject;
  OrigPrompt : string;
  s          : string;
  i          : integer;
  jsl        : TJAStrList;
begin
  Result := '';

  console.log('SendBufferToStorage: start, lockerID=' + aLockerID);  // $%^ ###

  MetaObj := TJSONObject.Create;
  MetaObj.AddPair(FN_LOCKER_ID, TJSONString.Create(aLockerID));

  // Scalar fields from buffer
  for i := 0 to LockerFieldNames.Count-1 do
  begin
    s := LockerFieldNames.ValueFromIndex[i];
    if MatchText(s, [FN_LOCKER_ID, FN_TAGS, FN_PARAMETERS, FN_AI_PROMPT]) then
      Continue;
    MetaObj.AddPair(s, TJSONString.Create(BufVal(FBuffer, s)));
  end;

  // Tags -- buffer holds plain CommaText; convert back to JSON array for DB
  jsl := TJAStrList.CreateFromJSONString(BufVal(FBuffer, FN_TAGS));
  MetaObj.AddPair(FN_TAGS, jsl.AsJSONArr);

console.log('>>> TLockerFieldBindings.SendBufferToStorage:');   // $%^ ###
console.log('>>> - jsl.CommaText: {'+jsl.CommaText+'}');
console.log('>>> - jsl.Text: {'+jsl.Text+'}');
console.log('>>> - FBuffer.CommaText: {'+FBuffer.CommaText+'}');
console.log('>>> - FBuffer.Text: {'+FBuffer.Text+'}');
console.log('>>> - jsl.StrictDelimiter=: {'+IfThen(jsl.StrictDelimiter,'T','F')+'}');   // $%^ ###

  // Parameters
  s := FBuffer.Values[FN_PARAMETERS];
  if (s = '') then s := '[]';
  ParamsArr := TJSONObject.ParseJSONValue(s) as TJSONArray;
  if not Assigned(ParamsArr) then ParamsArr := TJSONArray.Create;
  MetaObj.AddPair(FN_PARAMETERS, ParamsArr);

  // PUT metadata
  Request         := THTTPRequest.Create(nil);
  Request.URL     := LOCKER_METADATA_API_URL + '?lockerID=' + aLockerID;
  Request.Command := httpPUT;
  Request.Headers.AddPair('X-API-Key',    LOCKER_STORAGE_API_KEY);
  Request.Headers.AddPair('Content-Type', 'application/json');
  Request.PostData := MetaObj.ToJSON;

  console.log('PUT payload: ' + MetaObj.ToJSON);  // $%^ ###

  req := await(TJSXMLHttpRequest, Request.Perform());

  console.log('PUT response status: ' + IntToStr(req.Status));  // $%^ ###
  console.log('PUT response: ' + string(req.response));          // $%^ ###

  if req.Status <> 200 then
  begin
    Result := 'Error saving metadata: ' + IntToStr(req.Status) + ' ' + req.statusText;
    console.log('Status: ' + IntToStr(req.Status) + ' ' + req.statusText);
    console.log('Response: ' + string(req.response));
    Exit;
  end;

  // POST prompt only if changed
  OrigPrompt := '';
  if Assigned(FPromptJSON) then
    OrigPrompt := _JSON_VAL_STR(FPromptJSON, FN_AI_PROMPT);

  if BufVal(FBuffer, FN_AI_PROMPT) <> OrigPrompt then
  begin
    PromptObj := TJSONObject.Create;
    PromptObj.AddPair(FN_LOCKER_ID, TJSONString.Create(aLockerID));
    PromptObj.AddPair(FN_AI_PROMPT, TJSONString.Create(BufVal(FBuffer, FN_AI_PROMPT)));

    Request.URL      := LOCKER_PROMPTS_API_URL + '?lockerID=' + aLockerID;
    Request.PostData := PromptObj.ToJSON;

    req := await(TJSXMLHttpRequest, Request.Perform());

    if req.Status <> 200 then
      Result := 'Error saving prompt: ' + IntToStr(req.Status) + ' ' + req.statusText;
  end;
end;

// ── Accessors ────────────────────────────────────────────────────────────────

function TLockerFieldBindings.MetadataJSON: TJSONObject;
begin
  Result := FMetadataJSON;
end;

function TLockerFieldBindings.PromptJSON: TJSONObject;
begin
  Result := FPromptJSON;
end;

initialization
  if not Assigned(LockerFieldNames) then
    LockerFieldNames := TStringList.Create;
  LockerFieldNames.NameValueSeparator := ':';
  LockerFieldNames.AddStrings(FIELD_NAME_MAP);

end.
